import React, { useRef, useContext } from 'react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { company_logo, AmountInWords } from '../../../Utility/function';
import { WarehouseContext } from '../../../Utility/UserContext';

/**
 * ItemSalesInvoiceModal
 *
 * Props:
 *   invoiceData = {
 *     invoiceNo, orderId, date, time,
 *     customerName, customerPhone,
 *     products: [{ product_name, qty, unit_title, sell_price, discount_type, discount_value, total, offer_type }],
 *     subTotal, overDiscountAmount, amountDue, paymentType, returnTotal
 *   }
 *   onClose()
 */
const ItemSalesInvoiceModal = ({ invoiceData, onClose }) => {
  const { shopItem } = useContext(WarehouseContext);
  const invoiceRef = useRef(null);
  const [isPrinting, setIsPrinting] = React.useState(false);

  if (!invoiceData) return null;

  const {
    invoiceNo = '',
    orderId = '',
    date = new Date().toLocaleDateString(),
    time = new Date().toLocaleTimeString(),
    customerName = 'Walk-in Customer',
    customerPhone = '',
    products = [],
    subTotal = 0,
    overDiscountAmount = 0,
    amountDue = 0,
    paymentType = '1',
    returnTotal = 0,
  } = invoiceData;

  const companyInfo = {
    name: shopItem?.title || 'Company Name',
    address: shopItem?.Address || '',
    phone: shopItem?.phone1 || '',
    email: shopItem?.email || '',
  };

  const paymentLabels = {
    '1': { label: 'Cash', color: '#16a34a', bg: '#f0fdf4', icon: '💵' },
    '2': { label: 'Credit', color: '#d97706', bg: '#fffbeb', icon: '📒' },
    '3': { label: 'UPI', color: '#7c3aed', bg: '#f5f3ff', icon: '📲' },
    '4': { label: 'Card', color: '#2563eb', bg: '#eff6ff', icon: '💳' },
    '5': { label: 'Mix', color: '#0891b2', bg: '#ecfeff', icon: '🔀' },
  };
  const pm = paymentLabels[String(paymentType)] || paymentLabels['1'];

  /* ── Print as PDF (A4) ── */
  const handlePrintPDF = async () => {
    const content = invoiceRef.current;
    if (!content) return;
    setIsPrinting(true);
    try {
      const canvas = await html2canvas(content, { scale: 3, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const imgWidth = 190;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, 'PNG', (pdfWidth - imgWidth) / 2, 10, imgWidth, imgHeight);
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const pw = window.open(pdfUrl, '_blank');
      if (pw) pw.onload = () => pw.print();
      URL.revokeObjectURL(pdfUrl);
    } catch (err) {
      console.error('PDF print error:', err);
    } finally {
      setIsPrinting(false);
    }
  };

  /* ── Direct browser print (faster, cleaner) ── */
  const handleDirectPrint = () => {
    const content = invoiceRef.current;
    if (!content) return;
    const printWindow = window.open('', '_blank', 'width=900,height=700');
    if (!printWindow) return;
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice - ${invoiceNo || orderId}</title>
          <meta charset="UTF-8" />
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; font-size: 13px; color: #0f172a; background: white; padding: 24px; }
            @media print {
              body { padding: 10px; }
              @page { margin: 10mm; size: A4; }
            }
            .header-row { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
            .company-name { font-size: 18px; font-weight: 700; }
            .company-sub { font-size: 12px; color: #64748b; margin-top: 2px; }
            .badge { display: inline-block; background: #0f172a; color: white; padding: 3px 12px; border-radius: 4px; font-size: 11px; font-weight: 700; letter-spacing: 2px; margin-bottom: 8px; }
            .inv-meta { font-size: 12px; color: #64748b; }
            .inv-meta strong { color: #0f172a; }
            .divider { height: 2px; background: linear-gradient(90deg, #0f172a, #94a3b8, #f1f5f9); border-radius: 2px; margin: 14px 0; }
            .info-row { display: flex; gap: 16px; margin-bottom: 16px; }
            .info-box { flex: 1; border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px; }
            .info-label { font-size: 10px; color: #94a3b8; letter-spacing: 1px; text-transform: uppercase; font-weight: 700; margin-bottom: 4px; }
            .info-value { font-size: 14px; font-weight: 700; }
            .pm-box { border-color: ${pm.color}; background: ${pm.bg}; }
            .pm-value { color: ${pm.color}; }
            table { width: 100%; border-collapse: collapse; }
            thead tr { background: #0f172a; color: white; }
            th { padding: 9px 10px; font-size: 11px; font-weight: 700; }
            td { padding: 9px 10px; font-size: 12px; border-bottom: 1px solid #e2e8f0; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .text-left { text-align: left; }
            .striped { background: #f8fafc; }
            .free-badge { background: #dcfce7; color: #16a34a; font-size: 10px; padding: 1px 5px; border-radius: 3px; font-weight: 700; margin-left: 4px; }
            .summary { margin-left: auto; min-width: 260px; margin-top: 12px; }
            .summary-row { display: flex; justify-content: space-between; padding: 8px 14px; border-bottom: 1px solid #e2e8f0; font-size: 13px; }
            .grand-row { display: flex; justify-content: space-between; padding: 10px 14px; background: #0f172a; color: white; font-weight: 700; font-size: 14px; }
            .grand-amount { color: #4ade80; font-size: 16px; }
            .words-box { margin-top: 14px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px; font-size: 12px; color: #475569; }
            .footer { margin-top: 20px; border-top: 1px dashed #e2e8f0; padding-top: 12px; text-align: center; font-size: 12px; color: #94a3b8; }
            .footer strong { display: block; color: #0f172a; font-size: 13px; margin-bottom: 3px; }
          </style>
        </head>
        <body>
          ${content.innerHTML}
          <script>window.onload = function(){ window.print(); window.close(); }<\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Inline styles for the printable body (matched to print CSS above)
  const s = {
    body: { background: 'white', padding: '28px 32px', fontFamily: 'Arial, Helvetica, sans-serif' },
    headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' },
    companyName: { fontSize: '18px', fontWeight: 700, color: '#0f172a' },
    companySub: { fontSize: '12px', color: '#64748b', marginTop: '2px' },
    badge: {
      display: 'inline-block', background: '#0f172a', color: 'white',
      padding: '3px 14px', borderRadius: '5px', fontSize: '11px',
      fontWeight: 700, letterSpacing: '2px', marginBottom: '8px',
    },
    invMeta: { fontSize: '12px', color: '#64748b', lineHeight: '1.7' },
    divider: { height: '2px', background: 'linear-gradient(90deg, #0f172a, #94a3b8, #f1f5f9)', borderRadius: '2px', margin: '14px 0' },
    infoRow: { display: 'flex', gap: '16px', marginBottom: '16px' },
    infoBox: { flex: 1, border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 16px' },
    infoLabel: { fontSize: '10px', color: '#94a3b8', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 700, marginBottom: '4px' },
    infoValue: { fontSize: '15px', fontWeight: 700, color: '#0f172a' },
    pmBox: { border: `1.5px solid ${pm.color}`, background: pm.bg },
    pmValue: { color: pm.color },
    th: (align = 'center') => ({
      padding: '10px 12px', color: 'white', fontSize: '12px',
      fontWeight: 700, textAlign: align, whiteSpace: 'nowrap',
    }),
    td: (align = 'center', bold = false, color = '#334155', stripe = false) => ({
      padding: '9px 10px', fontSize: '12px', textAlign: align,
      fontWeight: bold ? 700 : 400, color,
      background: stripe ? '#f8fafc' : '#ffffff',
      borderBottom: '1px solid #e2e8f0',
    }),
    summaryWrap: { display: 'flex', justifyContent: 'flex-end', marginTop: '12px' },
    summaryBox: { minWidth: '280px', borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden' },
    summaryRow: { padding: '9px 16px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0' },
    grandRow: { padding: '11px 16px', display: 'flex', justifyContent: 'space-between', background: '#0f172a' },
    wordsBox: { marginTop: '14px', padding: '10px 14px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', color: '#475569' },
    footer: { marginTop: '22px', paddingTop: '14px', borderTop: '1px dashed #e2e8f0', textAlign: 'center', fontSize: '12px', color: '#94a3b8' },
  };

  return (
    <div
      className="modal fade show d-block"
      style={{ background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(6px)', zIndex: 1070 }}
      tabIndex="-1"
    >
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content border-0" style={{ borderRadius: '20px', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.3)' }}>

          {/* ── Modal Header ── */}
          <div className="modal-header border-0 px-4 py-3" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)' }}>
            <div className="d-flex align-items-center gap-3">
              <div style={{ width: 40, height: 40, borderRadius: '10px', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <i className="bi bi-receipt text-white fs-5" />
              </div>
              <div>
                <h5 className="modal-title text-white fw-bold mb-0" style={{ fontSize: '17px' }}>Sales Invoice</h5>
                <small className="text-slate-400" style={{ fontSize: '12px' }}>{invoiceNo || orderId}</small>
              </div>
            </div>
            <div className="d-flex gap-2 align-items-center">
              {/* Direct Print button */}
              <button
                className="btn btn-sm fw-bold px-3"
                style={{ background: 'rgba(255,255,255,0.12)', color: 'white', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', fontSize: '13px' }}
                onClick={handleDirectPrint}
                title="Quick browser print"
              >
                <i className="bi bi-printer me-1" />Print
              </button>
              {/* PDF Download button */}
              <button
                className="btn btn-sm fw-bold px-3"
                style={{ background: isPrinting ? '#334155' : 'rgba(74,222,128,0.2)', color: '#4ade80', borderRadius: '8px', border: '1px solid rgba(74,222,128,0.3)', fontSize: '13px' }}
                onClick={handlePrintPDF}
                disabled={isPrinting}
                title="Save as PDF"
              >
                {isPrinting ? (
                  <><span className="spinner-border spinner-border-sm me-1" />PDF…</>
                ) : (
                  <><i className="bi bi-file-earmark-pdf me-1" />PDF</>
                )}
              </button>
              <button type="button" className="btn-close btn-close-white" onClick={onClose} />
            </div>
          </div>

          {/* ── Scrollable invoice body ── */}
          <div className="modal-body p-0" style={{ overflowY: 'auto', maxHeight: '76vh', background: '#f8fafc' }}>
            <div ref={invoiceRef} style={s.body}>

              {/* Header */}
              <div style={s.headerRow}>
                <div className="d-flex align-items-center gap-3">
                  <img
                    src={company_logo || '/assets/images/tech_logo.webp'}
                    alt="Logo"
                    style={{ height: '52px', width: 'auto', objectFit: 'contain' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div>
                    <div style={s.companyName}>{companyInfo.name}</div>
                    {companyInfo.address && <div style={s.companySub}>{companyInfo.address}</div>}
                    {companyInfo.phone && <div style={s.companySub}>📞 {companyInfo.phone}</div>}
                    {companyInfo.email && <div style={s.companySub}>✉ {companyInfo.email}</div>}
                  </div>
                </div>
                <div className="text-end">
                  {/* <div style={s.badge}>SALES INVOICE</div> */}
                  <div style={s.invMeta}>
                    <div><strong style={{ color: '#0f172a' }}>Invoice No:</strong> {invoiceNo || orderId}</div>
                    {orderId && orderId !== invoiceNo && <div><strong style={{ color: '#0f172a' }}>Order ID:</strong> {orderId}</div>}
                    <div><strong style={{ color: '#0f172a' }}>Date:</strong> {date}</div>
                    <div><strong style={{ color: '#0f172a' }}>Time:</strong> {time}</div>
                  </div>
                </div>
              </div>

              {/* Gradient divider */}
              <div style={s.divider} />

              {/* Bill To + Payment */}
              <div style={s.infoRow}>
                <div style={s.infoBox}>
                  <div style={s.infoLabel}>Bill To</div>
                  <div style={s.infoValue}>{customerName}</div>
                  {customerPhone && <div style={{ fontSize: '12px', color: '#64748b', marginTop: '3px' }}>📞 {customerPhone}</div>}
                </div>
                <div style={{ ...s.infoBox, ...s.pmBox }}>
                  <div style={s.infoLabel}>Payment Method</div>
                  <div style={{ ...s.infoValue, ...s.pmValue }}>{pm.icon} {pm.label}</div>
                </div>
              </div>

              {/* Items Table */}
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#0f172a' }}>
                    <th style={s.th('center')}>#</th>
                    <th style={s.th('left')}>Item Name</th>
                    <th style={s.th('center')}>Qty</th>
                    <th style={s.th('right')}>Unit Price</th>
                    <th style={s.th('right')}>Discount</th>
                    <th style={s.th('right')}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p, idx) => {
                    const stripe = idx % 2 !== 0;
                    const discVal = parseFloat(p.discount_value || 0);
                    const discType = p.discount_type || '%';
                    const discDisplay = discVal > 0
                      ? `${discVal}${discType === '₹' || discType === 'INR' ? ' ₹' : '%'}`
                      : '—';
                    const isFree = p.offer_type === 'free';
                    const sellPrice = parseFloat(p.sell_price || p.sales_price || 0);
                    const total = parseFloat(p.total || p.amount_per_price || 0);
                    const unitTitle = (p.unit_title || '').trim();
                    const qtyDisplay = unitTitle ? `${p.qty} ${unitTitle}` : `${p.qty}`;

                    return (
                      <tr key={idx}>
                        <td style={s.td('center', false, '#64748b', stripe)}>{idx + 1}</td>
                        <td style={s.td('left', true, '#0f172a', stripe)}>
                          {p.product_name || p.product_title || `Item #${p.product_id || p.item_id}`}
                          {isFree && (
                            <span style={{ marginLeft: 6, fontSize: '10px', background: '#dcfce7', color: '#16a34a', padding: '1px 6px', borderRadius: '4px', fontWeight: 700 }}>FREE</span>
                          )}
                        </td>
                        <td style={s.td('center', false, '#334155', stripe)}>{qtyDisplay}</td>
                        <td style={s.td('right', false, '#334155', stripe)}>₹{sellPrice.toFixed(2)}</td>
                        <td style={{ ...s.td('right', false, '#64748b', stripe), fontSize: '11px' }}>{discDisplay}</td>
                        <td style={s.td('right', true, '#0f172a', stripe)}>
                          {isFree
                            ? <span style={{ color: '#16a34a' }}>₹0.00</span>
                            : `₹${total.toFixed(2)}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Summary */}
              <div style={s.summaryWrap}>
                <div style={s.summaryBox}>
                  <div style={s.summaryRow}>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>Subtotal</span>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>₹{parseFloat(subTotal).toFixed(2)}</span>
                  </div>
                  {parseFloat(returnTotal) > 0 && (
                    <div style={s.summaryRow}>
                      <span style={{ fontSize: '13px', color: '#64748b' }}>Return Adjustment</span>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#16a34a' }}>−₹{parseFloat(returnTotal).toFixed(2)}</span>
                    </div>
                  )}
                  {parseFloat(overDiscountAmount) > 0 && (
                    <div style={s.summaryRow}>
                      <span style={{ fontSize: '13px', color: '#64748b' }}>Overall Discount</span>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#ef4444' }}>−₹{parseFloat(overDiscountAmount).toFixed(2)}</span>
                    </div>
                  )}
                  <div style={s.grandRow}>
                    <span style={{ fontSize: '14px', color: 'white', fontWeight: 700 }}>Grand Total</span>
                    <span style={{ fontSize: '16px', color: '#4ade80', fontWeight: 800 }}>₹{parseFloat(amountDue).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Amount in Words */}
              <div style={s.wordsBox}>
                <strong style={{ color: '#0f172a' }}>Amount in Words: </strong>
                {AmountInWords(amountDue)}
              </div>

              {/* Footer */}
              <div style={s.footer}>
                <strong>Thank you for your purchase!</strong>
                <div>This is a computer-generated invoice and does not require a signature.</div>
              </div>

            </div>
          </div>

          {/* ── Modal Footer ── */}
          <div className="modal-footer border-0 px-4 py-3" style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
            <button
              type="button"
              className="btn btn-outline-secondary rounded-xl px-4 fw-semibold"
              style={{ fontSize: '13px' }}
              onClick={onClose}
            >
              Close
            </button>
            {/* <button
              type="button"
              className="btn rounded-xl px-4 fw-bold"
              style={{ background: 'white', color: '#0f172a', fontSize: '13px', border: '1.5px solid #0f172a' }}
              onClick={handleDirectPrint}
            >
              <i className="bi bi-printer-fill me-2" />Print
            </button>
            <button
              type="button"
              className="btn rounded-xl px-4 fw-bold"
              style={{ background: 'linear-gradient(135deg, #0f172a, #334155)', color: 'white', fontSize: '13px', border: 'none' }}
              onClick={handlePrintPDF}
              disabled={isPrinting}
            >
              {isPrinting ? (
                <><span className="spinner-border spinner-border-sm me-2" />Generating…</>
              ) : (
                <><i className="bi bi-file-earmark-pdf-fill me-2" />Download PDF</>
              )}
            </button> */}
          </div>

        </div>
      </div>
    </div>
  );
};

export default ItemSalesInvoiceModal;
